/**
 * DS-internal hook : l'orbite du tandem, que lisent la bulle (`<LiveTandemBubble>`) et le tandem de
 * la scène (`<LiveStage tandem>`). La règle est pure, dans `tandemOrbit` ; ce crochet la fait tourner.
 *
 * Il ne lit que le relais (`samLeads`) : un onglet changé à la main ne le touche pas. Chaque relais
 * fait un demi-tour, sur le pilote natif — en `entree` à la courbe de la marque vers le masteur, à la courbe
 * qui se voit partir vers Sam (`orbitEasing`), en `forwardDuration` quand l'appelant la donne ; en
 * mouvement réduit, l'arrivée est posée d'un coup. Il rend, pour chaque visage, son décalage, son
 * échelle et son recul (0 devant, 1 derrière : le voile ou l'estompe que le composant y met), qui
 * se peint devant (`samInFront`, échangé à mi-course) et si la vidéo peut se montrer (`showVideo`).
 *
 * **Android** : la vidéo y est une `SurfaceView`, qui ne suit pas une transformation animée. Pendant
 * l'orbite, le visage montre la photo du masteur, et sa vidéo revient à l'arrivée. Sur le web et sur
 * iOS, la vidéo suit.
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react"
import { Animated, Easing, Platform, type ViewStyle } from "react-native"
import { useUnistyles } from "react-native-unistyles"

import { useReduceMotion } from "./useReduceMotion"
import {
    courseAt,
    type OrbitCourse,
    orbitEasing,
    type OrbitFace,
    type OrbitPoint,
    orbitTrack,
    relayCourse,
    restingTurn,
    samInFrontAt,
    samLeadsAt,
} from "./tandemOrbit"

/**
 * Un temps figé, pour les stories : la part (0 à 1) de la course vers le relais en cours où l'orbite
 * s'arrête. `null` (défaut) : elle tourne.
 */
export const TandemOrbitStill = createContext<number | null>(null)

/** La géométrie du tandem : les places de Sam et de l'autre visage, dans un même repère. */
export type TandemOrbitGeometry = {
    sam: OrbitFace
    other: OrbitFace
    /** L'écart de l'arc à mi-course : celui qui recule s'écarte de lui, celui qui avance de l'opposé. */
    swing: OrbitPoint
}

/** Ce qu'un visage reçoit : son `transform` (le déplacer, puis le réduire en son centre) et son recul. */
export type TandemOrbitFaceStyle = {
    transform: NonNullable<Animated.WithAnimatedObject<ViewStyle>["transform"]>
    /** 0 devant, 1 derrière : le voile ou l'estompe que le composant y pose. */
    back: Animated.AnimatedInterpolation<number>
}

export type TandemOrbit = {
    /** `null` tant que la géométrie n'est pas connue (une scène pas encore mesurée). */
    sam: TandemOrbitFaceStyle | null
    other: TandemOrbitFaceStyle | null
    /** Sam se peint devant : l'échange se fait à mi-course. */
    samInFront: boolean
    /** La vidéo du masteur peut se montrer : pas pendant l'orbite sur Android. */
    showVideo: boolean
    /** L'orbite est posée : ce que dit Sam peut sortir de lui (il attend la fin de la course). */
    landed: boolean
    /** La phase de l'orbite, de 0 à 2 (0 : Sam devant, 1 : le masteur devant) : ce qui doit suivre le sens. */
    phase: ReturnType<typeof Animated.modulo>
}

export function useTandemOrbit(options: {
    samLeads: boolean
    geometry: TandemOrbitGeometry | null
    /** La plateforme dont la vidéo se juge (défaut : celle de l'appareil) ; un test la pose. */
    platform?: string
    /** La durée de la course vers Sam devant ; défaut `entree` (le retour vers le masteur garde `entree`). */
    forwardDuration?: number
}): TandemOrbit {
    const { samLeads, geometry, platform = Platform.OS, forwardDuration } = options
    const { theme } = useUnistyles()
    const reduceMotion = useReduceMotion()
    const still = useContext(TandemOrbitStill)

    const course = useRef<OrbitCourse | null>(null)
    if (course.current == null) {
        const start = restingTurn(samLeads)
        course.current = { from: start, to: start }
    }
    const turn = useRef(new Animated.Value(still != null ? courseAt(samLeads, still) : course.current.to)).current
    const phase = useMemo(() => Animated.modulo(turn, 2), [turn])
    const running = useRef(false)
    const wasStill = useRef(still != null)
    // État d'interface : qui se peint devant, et l'orbite en cours (la photo à la place de la vidéo).
    const [samInFront, setSamInFront] = useState(() =>
        samInFrontAt(still != null ? courseAt(samLeads, still) : course.current!.to)
    )
    const [moving, setMoving] = useState(false)

    useEffect(() => {
        const id = turn.addListener(({ value }) => setSamInFront(samInFrontAt(value)))
        return () => {
            turn.removeListener(id)
            turn.stopAnimation()
        }
    }, [turn])

    useEffect(() => {
        const current = course.current!
        const next = relayCourse(current, samLeads, running.current)
        course.current = next
        const settle = next !== current || wasStill.current
        wasStill.current = still != null

        if (still != null) {
            const at = courseAt(samLeads, still)
            turn.stopAnimation()
            turn.setValue(at)
            setSamInFront(samInFrontAt(at))
            return
        }
        if (reduceMotion) {
            turn.stopAnimation()
            turn.setValue(next.to)
            running.current = false
            setMoving(false)
            setSamInFront(samLeadsAt(next.to))
            return
        }
        if (!settle) return

        running.current = true
        setMoving(true)
        // Une course neuve arrête celle d'avant sur la même valeur : pas de nettoyage à chaque relais. Vers
        // Sam devant, la course se voit partir (`orbitEasing`) ; vers le masteur, la courbe de la marque.
        Animated.timing(turn, {
            toValue: next.to,
            duration: samLeads ? (forwardDuration ?? theme.motion.entree) : theme.motion.entree,
            easing: Easing.bezier(...orbitEasing(samLeads, theme.motion.easing)),
            useNativeDriver: Platform.OS !== "web",
        }).start(({ finished }) => {
            if (!finished) return
            running.current = false
            setMoving(false)
        })
    }, [samLeads, reduceMotion, still, turn, theme.motion, forwardDuration])

    const key = geometry != null ? JSON.stringify(geometry) : null
    const faces = useMemo(() => {
        if (geometry == null) return { sam: null, other: null }
        const style = (face: OrbitFace, isSam: boolean): TandemOrbitFaceStyle => {
            const track = orbitTrack(face, isSam, geometry.swing)
            const along = (outputRange: number[]) => phase.interpolate({ inputRange: track.inputRange, outputRange })
            return {
                transform: [
                    { translateX: along(track.x) },
                    { translateY: along(track.y) },
                    { scale: along(track.scale) },
                ],
                back: along(track.back),
            }
        }
        return { sam: style(geometry.sam, true), other: style(geometry.other, false) }
        // La géométrie se compare par ses nombres : un objet neuf à chaque rendu ne refait pas les pistes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, phase])

    const orbiting = moving || still != null
    // Le relais vient de changer et la course n'est pas encore partie (elle part dans l'effet) : pas encore
    // posée non plus, sans quoi ce qui attend l'arrivée se montrerait le temps d'une image.
    const relayPending = samLeadsAt(course.current!.to) !== samLeads
    return {
        ...faces,
        samInFront,
        showVideo: !(orbiting && platform === "android"),
        landed: !orbiting && !relayPending,
        phase,
    }
}
